import React, { useState, useEffect } from 'react';
import { INTERNAL_COMPANY, API_CONFIG } from '../../../config/constants';
import { isSucursalesSupported, markSucursalesUnsupported } from '../../../shared/services/sucursalesSupport';

type Props = {
  user?: any;
  companiesProp?: any[];
  onSave: (data: any) => void;
  onClose: () => void;
  selectedCompanyId?: string | number | undefined;
  selectedCompanyName?: string | undefined;
};

const UserFormInterno: React.FC<Props> = ({ user, companiesProp = [], onSave, onClose, selectedCompanyId, selectedCompanyName }) => {
  // 🔥 CRITICAL: Internal users ALWAYS belong to internal company, not the selected company context
  const internalCompany = companiesProp.find(c => c.name === INTERNAL_COMPANY.NAME);
  const internalCompanyId = internalCompany?.id ?? companiesProp[0]?.id ?? 0;
  
  const initialCompanyId = user?.companyId ?? internalCompanyId;
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'ANALYST', companyId: initialCompanyId, sucursalId: '' });
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [sucursalesSupported, setSucursalesSupported] = useState(isSucursalesSupported());

  useEffect(() => {
    if (user) {
      const cid = user.companyId ?? internalCompanyId;
      setForm({ 
        name: user.name || '', 
        email: user.email || '', 
        password: '', 
        role: user.role || 'ANALYST', 
        companyId: cid, 
        sucursalId: user.sucursalId || '' 
      });
    }
  }, [user, companiesProp, internalCompanyId]);

  // Fetch sucursales when component mounts or companyId changes
  useEffect(() => {
    const fetchSucursales = async () => {
      if (!form.companyId) return;

      if (!isSucursalesSupported()) {
        setSucursalesSupported(false);
        setSucursales([]);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/sucursales?empresaId=${form.companyId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.status === 404) {
          markSucursalesUnsupported();
          setSucursalesSupported(false);
          setSucursales([]);
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setSucursales(data);
        }
      } catch (error) {
        console.error('Error fetching sucursales:', error);
        setSucursales([]);
      }
    };

    fetchSucursales();
  }, [form.companyId]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-slate-900 p-6 rounded-lg w-96">
        <h3 className="text-lg font-semibold text-white mb-4">{user ? 'Editar Usuario Interno' : 'Nuevo Usuario Interno'}</h3>
        <div className="space-y-3">
          <input className="w-full p-2 rounded bg-slate-800 text-white" placeholder="Nombre" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
          <input className="w-full p-2 rounded bg-slate-800 text-white" type="email" placeholder="Email" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} />
          {!user && (
            <input 
              className="w-full p-2 rounded bg-slate-800 text-white" 
              type="password" 
              placeholder="Contraseña" 
              value={form.password} 
              onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))} 
            />
          )}
          <select className="w-full p-2 rounded bg-slate-800 text-white" value={form.role} onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}>
            <option value="ADMIN">ADMIN</option>
            <option value="ANALYST">ANALYST</option>
            <option value="CLIENT">CLIENT</option>
          </select>
          {/* Internal users ALWAYS belong to internal company */}
          <div className="w-full p-2 rounded bg-slate-800 text-slate-300">
            {INTERNAL_COMPANY.NAME} (Empresa Interna)
          </div>
          {/* Sucursal selector */}
          <select 
            className="w-full p-2 rounded bg-slate-800 text-white" 
            value={form.sucursalId} 
            onChange={e => setForm(prev => ({ ...prev, sucursalId: e.target.value }))}
            disabled={!sucursalesSupported}
          >
            <option value="">Seleccionar Sucursal (Opcional)</option>
            {sucursales.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {!sucursalesSupported && (
            <p className="text-xs text-amber-400">Sucursales no disponibles en este entorno.</p>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-slate-700 text-white rounded">Cancelar</button>
          <button onClick={() => {
            const payload = { 
              ...form, 
              userType: 'internal', 
              companyId: form.companyId,
              sucursalId: form.sucursalId || null // Send null if no sucursal selected
            };
            // Solo enviar password si no está vacío o si es nuevo usuario
            if (!user || form.password) {
              onSave(payload);
            } else {
              const { password, ...payloadWithoutPassword } = payload;
              onSave(payloadWithoutPassword);
            }
          }} className="px-4 py-2 bg-sky-500 text-white rounded">Guardar</button>
        </div>
      </div>
    </div>
  );
};

export default UserFormInterno;
